import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import {
  IconAdjustments,
  IconBarcode,
  IconBook2,
  IconBuilding,
  IconFileText,
  IconLifebuoy,
  IconRocket,
  IconUser,
} from '@tabler/icons-react';
import { Loader, Modal, Stack, Text, ProximityScope } from '@wapharma/design-system';
import { ADMIN_DATA_SOURCES } from '../constants/adminNavLabels';
import { useAuth } from '../contexts/AuthContext';
import { useAdminAccess } from '../hooks/useStaffRoles';
import { useGuide } from '../contexts/GuideContext';
import { openSetupWizard } from '../lib/setupShell';
import { canAccessInitialSetup } from '@wapharma/shared';
import { AccountSettingsSection } from './AccountSettingsSection';
import { NotificationSoundAccountToggle } from './NotificationSoundAccountToggle';
import { OrganizationLegalIdentityForm } from './OrganizationLegalIdentityForm';
import { StockAlertsSettingsSection } from './StockAlertsSettingsSection';
import { SopReferencesSection } from './SopReferencesSection';
import { TOURS_ENABLED } from '../tours/flags';
import styles from './SettingsOverlay.module.css';

const LazyExternalCatalogDataSourcePage = lazy(() =>
  import('../pages/ExternalCatalogDataSourcePage').then((m) => ({
    default: m.ExternalCatalogDataSourcePage,
  })),
);

export type SettingsSectionKey =
  | 'general'
  | 'company'
  | 'account'
  | 'journey'
  | 'setup'
  | 'data-sources'
  | 'references';

type Props = {
  opened: boolean;
  onClose: () => void;
  initialSection?: SettingsSectionKey;
  /** Deep-link SOP id when opening Références. */
  initialSopId?: string | null;
  /** Opens the report-issue wizard (hosted by AdminShell) above this modal. */
  onReportIssue: () => void;
};

/**
 * Settings modal — section rail hosts preferences + Compte + Références + Sources GTIN.
 * Parcours métier deep-links to OnboardingDock (no embedded pack-tab home).
 */
export function SettingsOverlay({
  opened,
  onClose,
  initialSection = 'general',
  initialSopId = null,
  onReportIssue,
}: Props) {
  const { user } = useAuth();
  const guide = useGuide();
  const roles = user?.roles ?? [];
  const access = useAdminAccess();
  const canDataSources = access.seeDataSources;
  const canInitialSetup = canAccessInitialSetup({
    roles,
    bootstrapAdmin: user?.bootstrapAdmin,
    foundationsComplete: guide.readiness?.foundationsComplete ?? false,
  });

  const sections: { key: SettingsSectionKey; label: string; icon: typeof IconAdjustments }[] = [
    { key: 'general', label: 'Préférences', icon: IconAdjustments },
    { key: 'company', label: 'Société', icon: IconBuilding },
    { key: 'account', label: 'Compte', icon: IconUser },
    { key: 'references', label: 'Références', icon: IconFileText },
    ...(canInitialSetup
      ? [{ key: 'setup' as const, label: 'Configuration initiale', icon: IconRocket }]
      : []),
    ...(TOURS_ENABLED
      ? [{ key: 'journey' as const, label: 'Parcours métier', icon: IconBook2 }]
      : []),
    ...(canDataSources
      ? [{ key: 'data-sources' as const, label: ADMIN_DATA_SOURCES.label, icon: IconBarcode }]
      : []),
  ];

  const [section, setSection] = useState<SettingsSectionKey>(
    initialSection === 'journey' ? 'general' : initialSection,
  );

  const openJourneyDock = () => {
    guide.reopenCompanion();
    onClose();
  };

  useEffect(() => {
    if (!opened) return;
    if (initialSection === 'journey') {
      if (TOURS_ENABLED) {
        openJourneyDock();
      } else {
        setSection('general');
      }
      return;
    }
    if (initialSection === 'setup') {
      if (canInitialSetup) {
        openSetupWizard();
        onClose();
      } else {
        setSection('general');
      }
      return;
    }
    if (initialSection === 'data-sources' && !canDataSources) {
      setSection('general');
      return;
    }
    setSection(initialSection);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open once per open+initialSection
  }, [opened, initialSection, canDataSources, canInitialSetup]);

  let body: ReactNode;
  if (section === 'general') {
    body = (
      <div className={styles.generalPane}>
        <Stack gap="lg" style={{ maxWidth: 480 }}>
          <Stack gap="xs">
            <Text size="sm" fw={600}>
              Notifications
            </Text>
            <NotificationSoundAccountToggle />
          </Stack>
          <StockAlertsSettingsSection />
        </Stack>
      </div>
    );
  } else if (section === 'company') {
    body = (
      <div className={styles.generalPane}>
        <Stack gap="lg" style={{ maxWidth: 480 }}>
          <OrganizationLegalIdentityForm />
        </Stack>
      </div>
    );
  } else if (section === 'account') {
    body = <AccountSettingsSection />;
  } else if (section === 'references') {
    body = <SopReferencesSection initialSopId={initialSopId} />;
  } else if (section === 'data-sources') {
    body = <LazyExternalCatalogDataSourcePage />;
  } else {
    body = null;
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Paramètres"
      size="xl"
      className={styles.panel}
      bodyClassName={styles.body}
    >
      {opened ? (
        <div className={styles.layout}>
          <ProximityScope axis="y" as="nav" className={styles.rail} aria-label="Sections des paramètres">
            {sections.map((s) => {
              const Icon = s.icon;
              const active = section === s.key;
              return (
                <button
                  key={s.key}
                  type="button"
                  data-wp-proximity-item=""
                  className={active ? `${styles.railItem} ${styles.railItemActive}` : styles.railItem}
                  aria-current={active ? 'true' : undefined}
                  onClick={() => {
                    if (s.key === 'journey') {
                      openJourneyDock();
                      return;
                    }
                    if (s.key === 'setup') {
                      openSetupWizard();
                      onClose();
                      return;
                    }
                    setSection(s.key);
                  }}
                >
                  <Icon size={16} stroke={1.7} aria-hidden />
                  <span>{s.label}</span>
                </button>
              );
            })}
            <div className={styles.railFooter}>
              <button type="button" data-wp-proximity-item="" className={styles.railItem} onClick={onReportIssue}>
                <IconLifebuoy size={16} stroke={1.7} aria-hidden />
                <span>Signaler un problème</span>
              </button>
            </div>
          </ProximityScope>
          <div className={styles.content}>
            <Suspense
              fallback={
                <div className={styles.loading}>
                  <Loader />
                </div>
              }
            >
              {body}
            </Suspense>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
